import React, { useEffect, useState } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../../components/SearchableSelect';
import { getTelematicsErrorChartOptions } from '../configs/chartConfigs';
import { generateRaymondErrorCodesDataFromAPI } from '../utils/dataGenerators';
import { getraymondtelematicserrorcodes } from '../../../service/telemetricDashboardService';
import { buildFilterObject } from '../utils/filterUtils';

const ErrorCodesChart = ({ chartFilters, selectedManufacturer }) => {
	const [errorCodesData, setErrorCodesData] = useState({ categories: [], series: [] });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedSbids, setSelectedSbids] = useState([]);
	const [selectedDescriptions, setSelectedDescriptions] = useState([]);
	const [sbidOptions, setSbidOptions] = useState([]);
	const [descriptionOptions, setDescriptionOptions] = useState([]);
	const [fullData, setFullData] = useState([]);

	useEffect(() => {
		// Only fetch for Raymond manufacturers
		if (selectedManufacturer !== 'raymond-us' && selectedManufacturer !== 'raymond-eu') {
			setLoading(false);
			return;
		}

		const fetchErrorCodesData = async () => {
			try {
				setLoading(true);
				setError(null);
				const filters = buildFilterObject(chartFilters);
				const response = await getraymondtelematicserrorcodes(selectedManufacturer, filters);
				const data = response.data;
				setFullData(data);
			} catch (err) {
				console.error('Error fetching error codes data:', err);
				setError('Failed to load error codes data');
			} finally {
				setLoading(false);
			}
		};

		fetchErrorCodesData();
	}, [chartFilters.siteids, chartFilters.vehiclemodels, chartFilters.vehicletypes, selectedManufacturer]);

	// Build filter options when fullData or selectedSbids change
	useEffect(() => {
		if (fullData.length === 0) return;

		const uniqueSbids = [...new Set(fullData.map(item => item.SBID))].sort().map(sbid => {
			const item = fullData.find(d => d.SBID === sbid);
			const desc = item ? item.DescriptionEnglish : '';
			return { value: sbid, label: desc ? `${sbid} - ${desc}` : sbid };
		});
		
		let dataForDescriptions = fullData;
		if (selectedSbids.length > 0) {
			dataForDescriptions = fullData.filter(item => selectedSbids.includes(item.SBID));
		}
		const uniqueDescriptions = [...new Set(dataForDescriptions.map(item => item.ErrorValue))].sort().map(ev => {
			const item = dataForDescriptions.find(d => d.ErrorValue === ev);
			const desc = item ? item.DescriptionEnglish : '';
			return { value: ev, label: desc ? `${desc} (${ev})` : ev };
		});
		
		setSbidOptions(uniqueSbids);
		setDescriptionOptions(uniqueDescriptions);
	}, [fullData, selectedSbids]);

	// Update chart data when fullData or local filters change
	useEffect(() => {
		if (fullData.length === 0) return;

		let filteredData = fullData;
		if (selectedSbids.length > 0) {
			filteredData = filteredData.filter(item => selectedSbids.includes(item.SBID));
		}
		if (selectedDescriptions.length > 0) {
			filteredData = filteredData.filter(item => selectedDescriptions.includes(item.ErrorValue));
		}

		const transformedData = generateRaymondErrorCodesDataFromAPI(filteredData, selectedDescriptions);
		setErrorCodesData(transformedData);
	}, [fullData, selectedSbids, selectedDescriptions]);

	const errorCodesOptions = getTelematicsErrorChartOptions(errorCodesData.categories);

	// Don't render for non-Raymond manufacturers
	if (selectedManufacturer !== 'raymond-us' && selectedManufacturer !== 'raymond-eu') {
		return null;
	}

	return (
		<Card className="mt-4">
			<Card.Header>
				<div className="d-flex justify-content-between align-items-center">
					<span>Error Codes (SBID: 8194)</span>
					<div className="d-flex align-items-center">
						<span className="badge bg-secondary">last 7 days</span>
					</div>
				</div>
			</Card.Header>
			<Card.Body>
				{/* Inline Filters for Error Codes */}
				<div className="d-flex align-items-center gap-3 mb-3 pb-2 border-bottom">
					<div className="d-flex align-items-center gap-2">
						<small className="text-muted fw-semibold text-nowrap">SBID:</small>
						<div style={{ minWidth: '150px', maxWidth: '250px' }}>
							<SearchableSelect
								options={sbidOptions}
								value={selectedSbids}
								onChange={setSelectedSbids}
								placeholder="Filter by SBID..."
								size="sm"
								isMulti={true}
								hideBadges={true}
							/>
						</div>
						<small className="text-muted" style={{ fontSize: '0.7rem' }}>
							{selectedSbids.length === 0 ? `All selected` : `${selectedSbids.length} selected`}
							{selectedSbids.length > 0 && <FontAwesomeIcon icon={faTimes} size="lg" style={{cursor: 'pointer', marginLeft: '0.25rem'}} onClick={() => setSelectedSbids([])} />}
						</small>
					</div>
					<div className="d-flex align-items-center gap-2">
						<small className="text-muted fw-semibold text-nowrap">Error Value:</small>
						<div style={{ minWidth: '200px', maxWidth: '350px' }}>
							<SearchableSelect
								options={descriptionOptions}
								value={selectedDescriptions}
								onChange={setSelectedDescriptions}
								placeholder="Filter by Error Value..."
								size="sm"
								isMulti={true}
								hideBadges={true}
							/>
						</div>
						<small className="text-muted" style={{ fontSize: '0.7rem' }}>
							{selectedDescriptions.length === 0 ? `All selected` : `${selectedDescriptions.length} selected`}
							{selectedDescriptions.length > 0 && <FontAwesomeIcon icon={faTimes} size="lg" style={{cursor: 'pointer', marginLeft: '0.25rem'}} onClick={() => setSelectedDescriptions([])} />}
						</small>
					</div>
				</div>
				<div className="mb-3">
					<small>X-axis: Time (7 days) | Y-axis: Vehicle Count | Type: Stacked Column Chart</small>
				</div>
				{loading ? (
					<div className="text-center py-5">
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</div>
				) : error ? (
					<Alert variant="danger">{error}</Alert>
				) : errorCodesData.series.length === 0 ? (
					<Alert variant="info">No error codes data available for the last 7 days</Alert>
				) : (
					<Chart
						options={errorCodesOptions}
						series={errorCodesData.series}
						type="bar"
						height={350}
					/>
				)}
			</Card.Body>
		</Card>
	);
};

export default ErrorCodesChart;
